import { Autocomplete, Box, Button, Divider, Drawer, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import React, { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as _ from 'lodash';
import {
  ClientFilter,
  FilterProps,
  useAffiliationPartnershipsQuery
} from '../../../shared';
import { TextInput } from '@coverright/ui/inputs';
import StatusFilter from '../../../features/status-filter';
import { AgentSelector, DateRange, PeriodDateRange, StateFilter, TagSelector, TimezoneFilter } from '../../../features';
import { X } from '@phosphor-icons/react';
import { AffiliationPartnershipOutput } from '@coverright/data-access/types/enrollment';

interface ClientListFilterDrawerProps {
  open: boolean,
  onClose: (filter?: ClientFilter) => void,
  initial?: ClientFilter
}

interface Props {
  open: (onCloseCallback: ClientListFilterDrawerProps['onClose'], filters?: ClientFilter) => void;
}

const ClientListFilterDrawerContext = React.createContext<Props>({
  open: () => {}
})

export function withClientListFilterDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<ClientFilter | undefined>();
    const callback = useRef<ClientListFilterDrawerProps['onClose']>();

    const onClose: ClientListFilterDrawerProps['onClose'] = filter => {
      setOpen(false);
      filter && callback.current && callback.current(filter);
    }

    const openDrawer = (onCloseCallback: ClientListFilterDrawerProps['onClose'], filter?: ClientFilter) => {
      setFilters(filter)
      callback.current = onCloseCallback;
      setOpen(true);
    }

    return <ClientListFilterDrawerContext.Provider value={{open: openDrawer}}>
      <ClientListFilterDrawer open={open} onClose={onClose} initial={filters}  />
      <WrappedComponent />
    </ClientListFilterDrawerContext.Provider>
  }
}

export const useClientListFilterDrawer = () => useContext(ClientListFilterDrawerContext)

function ClientListFilterDrawer({ open, onClose, initial }: ClientListFilterDrawerProps) {
  const [filters, setFilters] = useState<ClientFilter>(initial || {});
  useEffect(() => {
    if (open) {
      setFilters(initial || {});
    } else {
      setFilters({});
    }
  }, [open, initial]);

  const setFilterValues = (values: Array<{ name: keyof ClientFilter, value: any }>) => {
    setFilters(prev => {
      const result = prev ? { ...prev } : {};
      for (const value of values) {
        result[value.name] = value.value === '-' ? undefined : value.value || undefined;
      }
      return result;
    });
  };

  const onSearchClick = () => {
    onClose(filters)
  };

  return <Drawer
    anchor={'right'}
    open={open}
    onClose={() => onClose()}
  >
    <Box sx={{ p: 4, width: 500, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={'h3'}>Client list filters</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 3, mt: 1 }} />
      <Stack component={'form'} onSubmit={(e: any) => e.preventDefault()} spacing={3}>
        <PeriodDateRange value={filters.range || {}}
                   label={'Created'}
                   onChange={value => setFilterValues([{ name: 'range', value }])} />
        <StatusFilter label={'Status'} value={filters.status || ''} onChange={value => setFilterValues([{ name: 'status', value }])} />
        <CampaignFilter value={filters.marketingSource ? {
          campaign: filters.marketingCampaign!,
          source: filters.marketingSource
        } : undefined}
                        onChange={value => {
                          setFilterValues([
                            { name: 'marketingCampaign', value: value?.campaign },
                            { name: 'marketingSource', value: value?.source }
                          ]);
                        }} />
        <DateRange value={filters.birthDate}
                   label={'Birth date'}
                   onChange={value => setFilterValues([{ name: 'birthDate', value }])} />
        <PeriodDateRange value={filters.followUpDate || {}}
                         future
                         label={'Next contact date'}
                         onChange={value => setFilterValues([{ name: 'followUpDate', value }])} />
        <StateFilter value={filters.state || ''} onChange={value => setFilterValues([{ name: 'state', value }])} />
        <TimezoneFilter value={filters.timeZone || ''} onChange={value => setFilterValues([{ name: 'timeZone', value }])} />
        <UtmSourceFilter value={filters.utmSource || ''}
                         onChange={value => setFilterValues([{ name: 'utmSource', value }])} />
        <Stack gap={1}>
          <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600 }}>Tag type</Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size={'small'}
            value={filters.aiGeneratedTags === true ? 'ai' : filters.aiGeneratedTags === false ? 'manual' : 'all'}
            onChange={(_, val) => {
              if (val === null) return;
              setFilters(prev => ({
                ...(prev || {}),
                aiGeneratedTags: val === 'ai' ? true : val === 'manual' ? false : undefined,
              }));
            }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: 14,
                py: 0.75,
                fontWeight: 500,
                color: '#4B5563',
                border: '1px solid #dfeff3',
                '&.Mui-selected': {
                  bgcolor: '#f6fafb',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  fontWeight: 600,
                  zIndex: 1,
                  '&:hover': { bgcolor: '#eef6f8' },
                },
                '&.Mui-selected + .MuiToggleButton-root': {
                  borderLeftColor: 'primary.main',
                },
              },
            }}
          >
            <ToggleButton value={'all'}>All</ToggleButton>
            <ToggleButton value={'ai'}>AI tags</ToggleButton>
            <ToggleButton value={'manual'}>Manual tags</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <TagSelector selectedValues={filters.tags || []} label={'Tags'}
                     onChange={value => setFilterValues([{ name: 'tags', value: value.map(t => t.id) }])} />
        <AgentSelector showEmptyOption
                       value={filters.agentId}
                       onChange={value => setFilterValues([{ name: 'agentId', value }])} />
        <AdvisorFilter value={filters.adviserEmail || ''}
                         onChange={value => setFilterValues([{ name: 'adviserEmail', value }])} />

        <div>
          <Button variant={'contained'} type={'submit'} onClick={onSearchClick}>Search</Button>
        </div>
      </Stack>
    </Box>
  </Drawer>;
}

const CampaignFilter = ({ value, onChange }: FilterProps<AffiliationPartnershipOutput>) => {
  const [partnershipAutocompleteValue, setPartnershipAutocompleteValue] = useState('');
  const [partnership, setPartnership] = useState(value || null);
  const { data, loading } = useAffiliationPartnershipsQuery();

  const partnerships = useMemo(() => data?.affiliationPartnerships || [], [data]);

  useEffect(() => {
    setPartnership(value || null)
  }, [value]);

  const filteredPartnerships = useMemo(() => {
    return _.orderBy(partnerships, ['source', 'campaign'])
      .filter(p => `${p.source}: ${p.campaign}`.indexOf(partnershipAutocompleteValue) >= 0) || [];
  }, [partnerships, partnershipAutocompleteValue]);


  return <Autocomplete fullWidth
                       loading={loading}
                       options={filteredPartnerships}
                       value={partnership}
                       onInputChange={(event, newInputValue) => {
                         setPartnershipAutocompleteValue(newInputValue);
                       }}
                       getOptionLabel={(p: any) => {
                         return p ? `${p.source}: ${p.campaign || '-'}` : '';
                       }}
                       onChange={(event, value) => {
                         onChange(value as any);
                         setPartnership(value as any);
                       }}
                       clearOnBlur
                       renderInput={params => <TextInput
                         hideTick
                         placeholder={'CAMPAIGN'}
                         value={partnershipAutocompleteValue}
                         {...params} />} />;
};


const UtmSourceFilter = ({ value, onChange }: FilterProps<string>) => (
  <TextInput
    fullWidth
    hideTick
    name="utmSource"
    label="UtmSource"
    value={value}
    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value || '')}
  />
);


const AdvisorFilter = ({ value, onChange }: FilterProps<string>) => (
  <TextInput
    fullWidth
    hideTick
    name="adviserEmail"
    label="Advisor"
    placeholder={'Email or name'}
    value={value}
    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value || '')}
  />
);
